import axios from "axios";
import {getBackendURL, httpResponse, httpResponseError} from "../../fn";

/**
 * @typedef {Object} Document
 * @property {number} id - Уникальный идентификатор документа
 * @property {string} createdAt - Дата создания в формате ISO
 * @property {number} dealId - ID сделки
 * @property {string} fileName - Название файла
 * @property {string} fileDesc - Описание файла
 * @property {string} link - Ссылка на файл в CDN
 * @property {string} type - Тип документа (legalAct, attorney и т.д.)
 * @property {string} subtype - Подтип документа (purchaseSaleAgreement, attorney и т.д.)
 * @property {string|null} issuer - Издатель документа
 * @property {string|null} issueDate - Дата выпуска документа
 * @property {string|null} series - Серия документа
 * @property {string|null} number - Номер документа
 * @property {boolean} hasSignature - Есть ли подпись
 * @property {string|null} comment - Комментарий
 * @property {string|null} deleted - Дата удаления
 * @property {string|null} signature - Подпись
 * @property {string|null} signlink - Ссылка для подписания
 * @property {string|null} signAt - Дата подписания
 * @property {string|null} sendToSignAt - Дата отправки на подписание
 * @property {string|null} sentForSignTo - Кому отправлено на подписание
 * @property {string|null} signedby - Кем подписано
 * @property {string|null} internalSigner - Внутренний подписант
 * @property {string|null} internalSignStatus - Статус внутренней подписи
 * @property {string|null} facsimilelink - Ссылка на факсимиле
 * @property {boolean|null} onlySelfSig - Только собственная подпись
 * @property {boolean|null} noRR - Без Росреестра
 */

/**
 * @typedef {Object} XMLDocument
 * @property {number} id - Уникальный идентификатор XML
 * @property {string} createdAt - Дата создания в формате ISO
 * @property {number} dealId - ID сделки
 * @property {number} participantId - ID участника
 * @property {number} registerProcessId - ID процесса регистрации
 * @property {string} name - Название XML файла
 * @property {string} actionCode - Код действия (regTransferRightRealtyWoEGRN, regRealtyWOEGRN и т.д.)
 * @property {string} cdnLink - Ссылка на XML в CDN
 * @property {boolean} signed - Подписан ли XML
 * @property {string|null} signlink - Ссылка для подписания
 * @property {string|null} signAt - Дата подписания
 * @property {string|null} sendToSignAt - Дата отправки на подписание
 * @property {Object|null} values - Дополнительные значения
 */

/**
 * @typedef {Object} Representative
 * @property {string} lastName - Фамилия
 * @property {string} firstName - Имя
 * @property {string} middleName - Отчество
 */

/**
 * @typedef {Object} SignatureInfo
 * @property {Object} signme - Информация о подписании через SignMe
 * @property {null} signme.precheck - Пречек
 * @property {null} signme.userinfo - Информация о пользователе
 */

/**
 * @typedef {Object} ParticipantDocumentsForSign
 * @property {number} participantId - Уникальный идентификатор участника
 * @property {string} fio - ФИО или название организации
 * @property {Array} signState - Состояние подписания
 * @property {SignatureInfo} signature - Информация о подписи
 * @property {string|null} signProcessId - ID процесса подписания
 * @property {string|null} provider - Сервис
 * @property {string|null} externalSignLink - Ссылка для внешнего подписания
 * @property {Document[]} documents - Документы участника
 * @property {Representative|null} representive - Представитель (для организаций)
 * @property {XMLDocument[]} xmls - XML документы
 * @property {string} role - Роль в сделке (seller, buyer и т.д.)
 * @property {string} type - Тип участника (person, organization)
 */

/**
 * @typedef {Object} InternalSigner
 * @property {number} id - Уникальный идентификатор подписанта
 * @property {string} createdAt - Дата создания в формате ISO
 * @property {string} signeruuid - UUID подписанта
 * @property {string} name - ФИО подписанта
 * @property {string} expireat - Дата истечения срока действия в формате ISO
 * @property {string} branch - Филиал (All - все филиалы)
 */

/**
 * @typedef {Object} SigningData
 * @property {ParticipantDocumentsForSign[]} participantsDocuments - Данные участников и их документы
 * @property {InternalSigner[]} internalSigners - Внутренние подписанты
 * @property {Array} externalSign - Внешние подписи
 * @property {boolean} isBlockedForRegistration - Заблокирована ли регистрация
 * @property {Object} configs - Конфиги
 */

/**
 * Получить список участников и документов на подпись
 * @param dealId
 * @returns {Promise<SigningData>}
 */
export const getParticipantsDocsList = async (dealId) => {
  return await new Promise((resolve) => {
    axios.get(`${getBackendURL()}/sign/deal/${dealId}`, {
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
      },
    }).then((response) => {
      resolve(response.data)
    }).catch((error) => {
      resolve(httpResponseError(error));
    });
  });
}

/**
 * Отправить документы на подпись Физлицу
 * @param dealId
 * @param docsToSign
 * @param participantId
 * @param provider
 * @returns {HttpResponseError|HttpResponseOk}
 */
export const sendDocToPersonSign = async (dealId, participantId, provider)=> {
  return await new Promise((resolve) => {
    axios.post(`${getBackendURL()}/sign/${provider}/deal/${dealId}/participant/${participantId}/`, {}, {
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
      },
    }).then((response) => {
      resolve(httpResponse(response.data))
    }).catch((error) => {
      resolve(httpResponseError(error));
    });
  });
}

/**
 * Отправить документы на подпись Юрлицу
 * @param dealId
 * @param participantId
 * @returns {HttpResponseError|HttpResponseOk}
 */
export const sendDocToOrganizationSign = async (dealId, participantId)=> {
  return await new Promise((resolve) => {
    axios.post(`${getBackendURL()}/sign/deal/${dealId}/participant/${participantId}/organization/documents`, {}, {
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
      },
    }).then((response) => {
      resolve(httpResponse(response.data))
    }).catch((error) => {
      resolve(httpResponseError(error));
    });
  });
}

/**
 * Отозвать документы с подписания
 * @param dealId
 * @param participantId
 * @param provider
 * @returns {HttpResponseError|HttpResponseOk}
 */
export const sendRejectSign = async(dealId, participantId, provider) => {
  return await new Promise((resolve) => {
    axios.post(`${getBackendURL()}/sign/deal/${dealId}/participant/${participantId}/provider/${provider}/reject`, {}, {
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
      },
    }).then((response) => {
      resolve(httpResponse(response.data))
    }).catch((error) => {
      resolve(httpResponseError(error));
    });
  });
}

/**
 * Отозвать документы с подписания
 * @param dealId
 * @param participantId
 * @returns {HttpResponseError|HttpResponseOk}
 */
export const sendRejectExtSign = async(dealId, participantId) => {
  return await new Promise((resolve) => {
    axios.post(`${getBackendURL()}/sign/deal/${dealId}/participant/${participantId}/extreject`, {}, {
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
      },
    }).then((response) => {
      resolve(httpResponse(response.data))
    }).catch((error) => {
      resolve(httpResponseError(error));
    });
  });
}

/**
 * Проверить подписание документов
 * @param dealId
 * @param participantId
 * @param provider
 * @returns {HttpResponseError|HttpResponseOk}
 */
export const checkSigningStatus = async (dealId, participantId, provider) => {
  return await new Promise((resolve) => {
    axios.post(`${getBackendURL()}/sign/deal/${dealId}/participant/${participantId}/check/${provider}`, {}, {
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
      },
    }).then((response) => {
      resolve(httpResponse(response.data))
    }).catch((error) => {
      resolve(httpResponseError(error));
    });
  });
}

/**
 * Отправить на внутреннее подписание
 * @param dealId
 * @returns {HttpResponseError|HttpResponseOk}
 */
export const sendToInternalSign = async (dealId) => {
  return await new Promise((resolve) => {
    axios.post(`${getBackendURL()}/sign/${dealId}/internal/`, {}, {
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
      },
    }).then((response) => {
      resolve(httpResponse(response.data))
    }).catch((error) => {
      resolve(httpResponseError(error));
    });
  });
}

/**
 * Отправить отправить на подпись Гаранту
 * @param dealId
 * @param participantId
 * @param intSignerId
 * @returns {HttpResponseError|HttpResponseOk}
 */
export const sendToSignGuarantor = async (dealId, participantId, intSignerId) => {
  return await new Promise((resolve) => {
    axios.post(`${getBackendURL()}/sign/deal/${dealId}/participant/${participantId}/intsigner/${intSignerId}`, {}, {
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
      },
    }).then((response) => {
      resolve(httpResponse(response.data))
    }).catch((error) => {
      resolve(httpResponseError(error));
    });
  });
}

/**
 *
 * @param dealId
 * @param extSignerId
 * @returns {HttpResponseError|HttpResponseOk}
 */
export const deleteExtSignerFromDeal = async (dealId, extSignerId) => {
  return await new Promise((resolve) => {
    axios.delete(`${getBackendURL()}/sign/signer/${extSignerId}/deal/${dealId}`, {}, {
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
      },
    }).then((response) => {
      resolve(httpResponse(response.data))
    }).catch((error) => {
      resolve(httpResponseError(error));
    });
  });
}

/**
 * Получить данные по регистрации субъекта в провайдерах подписания
 * @param id
 * @returns {Promise<HttpResponseOk|HttpResponseError>}
 */
export const getSubjectSignaturesInfo = async (id) => {
  return await new Promise((resolve) => {
    axios.get(`${getBackendURL()}/sign/info/${id}`, {
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
      },
    }).then((response) => {
      resolve(httpResponse(response.data));
    }).catch((error) => {
      resolve(httpResponseError(error));
    });
  });
}
